import React from "react";
import { getColorClasses } from "../../utils/getBackgroundColor";
import { EditorialBlock as EditorialBlockType } from "../../types/optimizely/blocks";
import classNames from "classnames";
import dompurify from "dompurify";
import { renderToString } from "react-dom/server";
import ArrowRight from "../../assets/arrow-right.svg?react";
import logo from "../../assets/wilhelmsen_logo_flag.svg";
import { isHeaderTag } from "../../utils/isHeaderTag";
import { getHeaderClasses, getHeaderClassesMap } from "../../utils/getHeaderClasses";
import { sanitizeOrPassthrough } from "../../helpers/rteHelpers";
import { SsrTagClassMap } from "../../helpers/rteSsrSanitizer";

interface EditorialBlockProps extends EditorialBlockType {}

const getAnchorClasses = (backgroundColor: string) =>
  `w-full bg-transparent ${backgroundColor} hover:${backgroundColor} items-center font-bold rounded flex justify-center`;
const localPurifier = dompurify();

const EditorialBlock = ({
  title,
  text,
  backgroundColor: optiBgColor,
  sectionId,
}: EditorialBlockProps) => {
  const backgroundColor = getColorClasses(optiBgColor?.value);

  // Mirrors the "beforeSanitizeAttributes" hook below, for SSR markup to match the client.
  const ssrTagClasses: SsrTagClassMap = {
    a: `${getAnchorClasses(backgroundColor)} font-normal group hover:text-primaryBlue`,
    ...getHeaderClassesMap((level) => `${getHeaderClasses(level)} ${backgroundColor} font-semibold`),
  };

  localPurifier.addHook("beforeSanitizeAttributes", (node) => {
    if (node.nodeName && node.nodeName === "A") {
      const hasSvgChild =
        Array.from(node.childNodes).filter((child) => child.nodeName === "SPAN")
          .length > 0;
      node.setAttribute("class", ssrTagClasses.a);

      if (!hasSvgChild) {
        const icon = renderToString(
          <span className="ml-2 group-hover:translate-x-2 transition-all ease-out">
            <ArrowRight />
          </span>
        );
        node.innerHTML += icon;
      }
    }

    if (isHeaderTag(node)) {
      node.setAttribute("class", ssrTagClasses[node.nodeName.toLowerCase()]);
    }
  });

  return (
    <div
      id={sectionId?.value || undefined}
      className={classNames(
        "container flex flex-col whitespace-pre-wrap ",
        backgroundColor
      )}
    >
      <div
        className={classNames(
          "text-2xl md:text-md font-semibold text-center md:w-[70%] self-center px-6 md:px-0 mb-8",
          backgroundColor
        )}
        dangerouslySetInnerHTML={{
          __html: sanitizeOrPassthrough(localPurifier, title.value, { ssrTagClassMap: ssrTagClasses }),
        }}
      />
      <div
        className={classNames(
          "text-md text-center md:w-[70%] self-center px-6 md:px-0",
          backgroundColor
        )}
        dangerouslySetInnerHTML={{
          __html: sanitizeOrPassthrough(localPurifier, text.value, { ssrTagClassMap: ssrTagClasses }),
        }}
      />
    </div>
  );
};

export default EditorialBlock;
